// The pure half of the module shell (05b) — main.js createModule's sums and markup,
// split out so they're unit-testable without a DOM.

import type { RawPlayerRecord } from "../data/snapshot.js";
import { comparePlayersByName, getPlayerShortName } from "../data/player-utils.js";
import { playerById, playerIdOf, type PlayerId } from "../data/players.js";
import type { PickedPlayer } from "../live/picked-players.js";

// main.js's grid (GRID_SIZE / GRID_GAP) — the one copy since main.js went (05k-1).
export const GRID_SIZE = 100;
export const GRID_GAP = 10;

/** main.js getPixelSize: a span of `units` grid cells in px (cells + the gaps between them). */
export function pixelSize(units: number): number {
  return units * GRID_SIZE + (units - 1) * GRID_GAP;
}

/** The `.placed-module` class list. The slug class is fixed at creation — a later type
 *  switch (League Table → Squad) changes the title but, as in main.js, not this class. */
export function moduleClassName(slug: string): string {
  return slug ? `placed-module module-${slug}` : "placed-module";
}

export interface PlayerStrip {
  /** The player the module opens on; undefined only if no player is eligible. */
  initialPlayer: PlayerId | undefined;
  /** The `<option>` list for the hidden `.player-select`, exactly as main.js wrote it —
   *  except each option's value is the player's id (ids-1), not her name. */
  optionsHtml: string;
}

/** The ids a module's picker offers: Goalkeeping only goalkeepers; surname order. */
export function eligiblePlayerIds(players: Record<string, RawPlayerRecord>, categoryId: string | undefined): PlayerId[] {
  return Object.keys(players)
    .filter((n) => categoryId !== "goalkeeping" || players[n]?.positionTemplate === "goalkeeper")
    .sort((a, b) => comparePlayersByName(players[a] ?? {}, players[b] ?? {}))
    .map((n) => playerIdOf(players[n] as RawPlayerRecord));
}

/** Who a new module opens on: an explicit player (undo / preset) wins, then the sticky
 *  last pick, then the first one — if they're on offer. */
export function openingPlayer(ids: PlayerId[], playerVal: PlayerId | null, lastSelected: PlayerId | null): PlayerId | undefined {
  if (playerVal !== null && ids.includes(playerVal)) return playerVal;
  if (lastSelected !== null && ids.includes(lastSelected)) return lastSelected;
  return ids[0];
}

/**
 * The `.player-select`'s value as a PlayerId. The select holds the id as a string (an
 * <option> value is always text); "" = no player (an empty picker). Anything else that
 * isn't an id is a name that slipped through — a bug, so it throws.
 */
export function playerIdFromSelect(value: string): PlayerId | null {
  if (value === "") return null;
  const id = Number(value);
  if (!Number.isInteger(id) || id <= 0) throw new Error(`.player-select holds "${value}", not a player id`);
  return id;
}

/** Text safe inside HTML (an attribute or an element) — a live player's name comes from
 *  the DB, not from the snapshot file we ship. */
function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * The ids a picker offers: the snapshot's eligible players, then any live-picked players
 * the snapshot doesn't have (ids-3 — `extras`, always empty on the snapshot site). They go
 * last, so a picker with no sticky / explicit player still opens on the snapshot's first.
 * Their position isn't known yet, so every picker offers them.
 */
export function offeredPlayerIds(
  players: Record<string, RawPlayerRecord>,
  categoryId: string | undefined,
  extras: readonly PickedPlayer[] = [],
): PlayerId[] {
  const ids = eligiblePlayerIds(players, categoryId);
  return [...ids, ...extras.map((p) => p.id).filter((id) => !ids.includes(id))];
}

/**
 * main.js createModule's player picker for a needsPlayer module: the eligible players
 * (Goalkeeping only offers goalkeepers) in surname order, and the player to open on —
 * an explicit one (undo / preset) wins, then the sticky last pick, then the first one.
 * `extras` (live only): players picked from the real search that the snapshot doesn't
 * have, so Undo or the sticky pick can open a module on her.
 */
export function buildPlayerStrip(
  players: Record<string, RawPlayerRecord>,
  categoryId: string | undefined,
  playerVal: PlayerId | null,
  lastSelected: PlayerId | null,
  extras: readonly PickedPlayer[] = [],
): PlayerStrip {
  const ids = eligiblePlayerIds(players, categoryId);
  const initialPlayer = openingPlayer(offeredPlayerIds(players, categoryId, extras), playerVal, lastSelected);
  // An HTML string, not JSX (05c's fork 2A): it keeps the DOM identical (incl. the
  // `selected` attribute React won't write) and 1,100 options as one string cost React
  // nothing to diff. PlayerStrip fills the select with it once.
  const optionsHtml = ids
    .map((id) => {
      const player = playerById(players, id) ?? {};
      const searchText = [
        getPlayerShortName(player),
        player.name,
        player.nickname,
        player.team,
        player.competition,
        player.country,
      ]
        .filter(Boolean)
        .join(" ")
        .replace(/"/g, "&quot;");
      return `<option value="${id}" data-search="${searchText}" ${id === initialPlayer ? "selected" : ""}>${getPlayerShortName(player)}</option>`;
    })
    .join("");
  const extrasHtml = extras
    .filter((p) => !ids.includes(p.id))
    .map((p) => {
      const searchText = escapeHtml([p.name, p.team].filter(Boolean).join(" "));
      return `<option value="${p.id}" data-search="${searchText}" ${p.id === initialPlayer ? "selected" : ""}>${escapeHtml(p.name)}</option>`;
    })
    .join("");
  return { initialPlayer, optionsHtml: optionsHtml + extrasHtml };
}
